import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Breadcrumbs,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import StoreIcon from "@mui/icons-material/Store";
import SaveIcon from "@mui/icons-material/Save";
import ImageIcon from "@mui/icons-material/Image";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { resolveLogoSrc } from "../utils/resolveLogoSrc";

const createInitialShop = () => ({
  name: "",
  shopCode: "",
  licenseNumber: "",
  taxId: "",
  address: "",
  phone: "",
  email: "",
  logo: "",
});

const createGoogleCalendarStatus = () => ({
  configured: false,
  connected: false,
  syncEnabled: false,
  calendarId: "primary",
  accountEmail: "",
  defaultDurationMinutes: 60,
  connectedAt: null,
  lastSyncError: "",
});

const normalizeShop = (shop = {}) => {
  const address =
    shop.address ||
    shop.shop_address ||
    shop.full_address ||
    [shop.address_line1, shop.address_line2, shop.city, shop.state, shop.zip]
      .filter(Boolean)
      .join(", ");
  const phone = shop.phone || shop.shop_phone || shop.contact_phone || "";

  return {
    ...createInitialShop(),
    ...shop,
    address,
    phone,
    shopCode: shop.shopCode || "",
    licenseNumber: shop.licenseNumber || "",
    taxId: shop.taxId || "",
    logo: shop.logo || "",
  };
};

function ShopProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [shop, setShop] = useState(createInitialShop());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [googleCalendar, setGoogleCalendar] = useState(createGoogleCalendarStatus());
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  const getShop = useCallback(() => {
    setLoading(true);
    axios
      .get(`${Helpers.baseUrl}/shops/current`, Helpers.headers)
      .then((response) => {
        const normalizedShop = normalizeShop(response.data || {});
        setShop(normalizedShop);
        setCreateMode(false);
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          setShop(createInitialShop());
          setCreateMode(true);
          return;
        }
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    getShop();
  }, [getShop]);

  const getGoogleCalendarStatus = useCallback(() => {
    setCalendarLoading(true);
    axios
      .get(`${Helpers.baseUrl}/google-calendar/status`, Helpers.headers)
      .then((response) => {
        setGoogleCalendar({
          ...createGoogleCalendarStatus(),
          ...(response.data || {}),
        });
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setCalendarLoading(false));
  }, [navigate]);

  useEffect(() => {
    getGoogleCalendarStatus();
  }, [getGoogleCalendarStatus]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const status = query.get("googleCalendar");
    if (!status) return;

    if (status === "connected") {
      Helpers.toast("success", "Google Calendar connected");
      getGoogleCalendarStatus();
    } else {
      Helpers.toast("error", "Google Calendar connection failed");
    }
  }, [getGoogleCalendarStatus, location.search]);

  const handleChange = (field) => (event) => {
    setShop((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setLogoError("Please select an image file.");
      event.target.value = "";
      return;
    }

    const maxSizeBytes = 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setLogoError("Please use an image under 1 MB.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setShop((prev) => ({
        ...prev,
        logo: typeof reader.result === "string" ? reader.result : prev.logo,
      }));
      setLogoError("");
    };
    reader.onerror = () => {
      setLogoError("Could not read that image file.");
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleGoogleCalendarChange = (field) => (event) => {
    const value =
      field === "syncEnabled"
        ? event.target.checked
        : event.target.value;

    setGoogleCalendar((prev) => ({ ...prev, [field]: value }));
  };

  const saveGoogleCalendarSettings = () => {
    setCalendarSaving(true);
    axios
      .post(
        `${Helpers.baseUrl}/google-calendar/settings`,
        {
          calendarId: googleCalendar.calendarId || "primary",
          defaultDurationMinutes: googleCalendar.defaultDurationMinutes,
          syncEnabled: googleCalendar.syncEnabled,
        },
        Helpers.headers,
      )
      .then((response) => {
        setGoogleCalendar({
          ...createGoogleCalendarStatus(),
          ...(response.data || {}),
        });
        Helpers.toast("success", "Google Calendar settings saved");
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setCalendarSaving(false));
  };

  const connectGoogleCalendar = () => {
    setCalendarConnecting(true);
    axios
      .get(`${Helpers.baseUrl}/google-calendar/connect`, Helpers.headers)
      .then((response) => {
        if (response.data?.url) {
          window.location.href = response.data.url;
          return;
        }
        Helpers.toast("error", "Could not start Google Calendar connection");
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
        setCalendarConnecting(false);
      });
  };

  const disconnectGoogleCalendar = () => {
    if (!window.confirm("Disconnect Google Calendar from this shop?")) return;

    setCalendarSaving(true);
    axios
      .post(`${Helpers.baseUrl}/google-calendar/disconnect`, {}, Helpers.headers)
      .then((response) => {
        setGoogleCalendar({
          ...createGoogleCalendarStatus(),
          ...(response.data || {}),
        });
        Helpers.toast("success", "Google Calendar disconnected");
      })
      .catch((error) => {
        Helpers.error_response(error);
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setCalendarSaving(false));
  };

  const handleSave = () => {
    const trimmedName = String(shop.name || "").trim();
    if (!trimmedName) {
      Helpers.toast("error", "Shop name is required");
      return;
    }

    setSaving(true);
    const payload = {
      ...shop,
      name: trimmedName,
      shopCode: String(shop.shopCode || "").trim(),
      licenseNumber: String(shop.licenseNumber || "").trim(),
      taxId: String(shop.taxId || "").trim(),
      address: String(shop.address || "").trim(),
      phone: String(shop.phone || "").trim(),
      email: String(shop.email || "").trim(),
      logo: String(shop.logo || "").trim(),
    };

    axios
      .post(
        `${Helpers.baseUrl}/shops/${createMode ? "create" : "update"}`,
        payload,
        Helpers.headers,
      )
      .then((response) => {
        const normalizedShop = normalizeShop(response.data || payload);
        setShop(normalizedShop);
        setCreateMode(false);
        Helpers.authShop = normalizedShop;
        localStorage.setItem("shop", JSON.stringify(normalizedShop));
        Helpers.toast("success", "Shop profile saved successfully");
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => setSaving(false));
  };

  const backendShopId = useMemo(
    () => shop?._id || "Will be assigned after creation",
    [shop?._id],
  );
  const logoSrc = useMemo(() => resolveLogoSrc(shop.logo), [shop.logo]);

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: "96px",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #0f766e 0%, #0ea5e9 55%, #2563eb 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255, 255, 255, 0.06)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </MuiLink>
            <MuiLink
              component={Link}
              to="/user/settings"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Settings
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Shop Profile
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              position: "relative",
              zIndex: 1,
              flexWrap: "wrap",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <StoreIcon sx={{ fontSize: 38, color: "white" }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: "white", fontWeight: 700, mb: 0.5 }}
                >
                  Shop Profile
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
                >
                  Manage the public and internal details for your shop.
                </Typography>
              </Box>
            </Stack>

            {/* <Chip
              label={`Backend ID: ${backendShopId}`}
              size="small"
              sx={{
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            /> */}
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            p: 3,
            maxWidth: 980,
            mb: 3,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Shop Summary
          </Typography>

          <Stack spacing={2.25}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}>
                Shop Name
              </Typography>
              <Typography variant="body1" sx={{ color: "#101828" }}>
                {shop.name || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}>
                Shop ID
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#101828", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all", flex: 1, minWidth: 0 }}
              >
                {backendShopId}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}>
                License Number
              </Typography>
              <Typography variant="body1" sx={{ color: "#101828" }}>
                {shop.licenseNumber || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}>
                Tax ID
              </Typography>
              <Typography variant="body1" sx={{ color: "#101828" }}>
                {shop.taxId || "-"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            p: 3,
            maxWidth: 980,
            mb: 3,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Editable Shop Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update the details used across the system for receipts, reports, and internal records.
            Shop name and shop ID are read-only after the shop has been created.
          </Typography>

          <Stack spacing={2.25}>
            {createMode && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}
                >
                  Name
                </Typography>
                <TextField
                  size="small"
                  value={shop.name}
                  onChange={handleChange("name")}
                  disabled={loading}
                  helperText="Set the shop name during initial creation. After creation, it becomes read-only here."
                  sx={{ flex: 1, minWidth: 280 }}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 140, pt: 1 }}
              >
                Address
              </Typography>
              <TextField
                size="small"
                multiline
                minRows={2}
                value={shop.address}
                onChange={handleChange("address")}
                disabled={loading}
                sx={{ flex: 1, minWidth: 280 }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}
              >
                Phone
              </Typography>
              <TextField
                size="small"
                value={shop.phone}
                onChange={handleChange("phone")}
                disabled={loading}
                sx={{ flex: 1, minWidth: 280 }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 140 }}
              >
                Email
              </Typography>
              <TextField
                type="email"
                size="small"
                value={shop.email}
                onChange={handleChange("email")}
                helperText="Customer email replies go here. Verify this address using the Email button on an estimate or receipt before sending."
                disabled={loading}
                sx={{ flex: 1, minWidth: 280 }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 140, pt: 1 }}
              >
                Logo
              </Typography>
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Box
                    sx={{
                      width: 180,
                      height: 84,
                      border: "1px solid #d0d5dd",
                      borderRadius: 1.5,
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt="Shop logo preview"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Stack spacing={0.75} alignItems="center" sx={{ color: "#98a2b3" }}>
                        <ImageIcon />
                        <Typography variant="caption">No logo</Typography>
                      </Stack>
                    )}
                  </Box>

                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      disabled={loading}
                      sx={{ textTransform: "none", width: "fit-content" }}
                    >
                      Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleLogoUpload}
                      />
                    </Button>

                    <TextField
                      size="small"
                      value={shop.logo}
                      onChange={(event) => {
                        setLogoError("");
                        handleChange("logo")(event);
                      }}
                      disabled={loading}
                      helperText={
                        logoError ||
                        "Upload an image or paste a logo URL / stored asset path."
                      }
                    />
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading || saving}
              sx={{
                textTransform: "none",
                backgroundColor: "#28a745",
                "&:hover": { backgroundColor: "#218838" },
              }}
            >
              {saving ? "Saving..." : createMode ? "Create Shop Profile" : "Save Shop Profile"}
            </Button>
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            p: 3,
            maxWidth: 980,
            mb: 3,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarMonthIcon sx={{ color: "#2563eb" }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Google Calendar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sync shop appointments to one Google Calendar.
                </Typography>
              </Box>
            </Stack>
            <Chip
              color={googleCalendar.connected ? "success" : "default"}
              label={googleCalendar.connected ? "Connected" : "Not connected"}
              size="small"
            />
          </Stack>

          {!googleCalendar.configured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Google Calendar OAuth is not configured on the server yet.
            </Alert>
          ) : null}

          {createMode ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Create the shop profile before connecting Google Calendar.
            </Alert>
          ) : null}

          <Stack spacing={2.25}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 160, pt: 1 }}
              >
                Google Account
              </Typography>
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {googleCalendar.accountEmail || "No Google account connected"}
                </Typography>
                {googleCalendar.connectedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Connected {new Date(googleCalendar.connectedAt).toLocaleString()}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 160 }}
              >
                Calendar ID
              </Typography>
              <TextField
                size="small"
                value={googleCalendar.calendarId || "primary"}
                onChange={handleGoogleCalendarChange("calendarId")}
                disabled={calendarLoading || createMode}
                helperText='Use "primary" for the connected account calendar, or paste another calendar ID.'
                sx={{ flex: 1, minWidth: 280 }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 160 }}
              >
                Event Length
              </Typography>
              <TextField
                type="number"
                size="small"
                value={googleCalendar.defaultDurationMinutes}
                onChange={handleGoogleCalendarChange("defaultDurationMinutes")}
                disabled={calendarLoading || createMode}
                inputProps={{ min: 15, max: 480, step: 15 }}
                helperText="Appointments do not have an end time, so Google events use this length."
                sx={{ flex: 1, minWidth: 280 }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "#667085", fontWeight: 600, minWidth: 160 }}
              >
                Sync
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(googleCalendar.syncEnabled)}
                    onChange={handleGoogleCalendarChange("syncEnabled")}
                    disabled={
                      calendarLoading ||
                      createMode ||
                      !googleCalendar.connected
                    }
                  />
                }
                label="Create and update Google Calendar events for appointments"
                sx={{ flex: 1, minWidth: 280, m: 0 }}
              />
            </Box>

            {googleCalendar.lastSyncError ? (
              <Alert severity="error">{googleCalendar.lastSyncError}</Alert>
            ) : null}
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              startIcon={<CalendarMonthIcon />}
              onClick={connectGoogleCalendar}
              disabled={
                calendarConnecting ||
                calendarLoading ||
                createMode ||
                !googleCalendar.configured
              }
              sx={{ textTransform: "none" }}
            >
              {googleCalendar.connected ? "Reconnect Google Calendar" : "Connect Google Calendar"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={saveGoogleCalendarSettings}
              disabled={calendarSaving || calendarLoading || createMode}
              sx={{ textTransform: "none" }}
            >
              {calendarSaving ? "Saving..." : "Save Calendar Settings"}
            </Button>
            {googleCalendar.connected ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={disconnectGoogleCalendar}
                disabled={calendarSaving || calendarLoading}
                sx={{ textTransform: "none" }}
              >
                Disconnect
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default ShopProfile;
